import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, MoveUp, MoveDown, Save, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { carouselAPI } from '../../utils/api';
import { ImageUploader } from './ImageUploader';

export const CarouselManager = () => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newImage, setNewImage] = useState({
    url: '',
    title: '',
    order: 0,
    active: true
  });

  const fetchImages = useCallback(async () => {
    try {
      const response = await carouselAPI.getAllAdmin();
      setImages(response.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error fetching carousel images:', error);
      toast({
        title: "Error",
        description: "Failed to load carousel images.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch carousel images
  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleAdd = async () => {
    try {
      await carouselAPI.create(newImage);
      toast({
        title: "Success",
        description: "Carousel image added successfully.",
      });
      setIsAdding(false);
      setNewImage({ url: '', title: '', order: 0, active: true });
      fetchImages();
    } catch (error) {
      console.error('Error adding image:', error);
      toast({
        title: "Error",
        description: "Failed to add carousel image.",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (img) => {
    setEditingId(img.id);
    setEditForm({ ...img });
  };

  const handleSaveEdit = async () => {
    try {
      await carouselAPI.update(editingId, editForm);
      toast({
        title: "Success",
        description: "Carousel image updated successfully.",
      });
      setEditingId(null);
      setEditForm({});
      fetchImages();
    } catch (error) {
      console.error('Error updating image:', error);
      toast({
        title: "Error",
        description: "Failed to update carousel image.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this carousel image?')) {
      try {
        await carouselAPI.delete(id);
        toast({
          title: "Success",
          description: "Carousel image deleted successfully.",
        });
        fetchImages();
      } catch (error) {
        console.error('Error deleting image:', error);
        toast({
          title: "Error",
          description: "Failed to delete carousel image.",
          variant: "destructive"
        });
      }
    }
  };

  const handleMoveUp = async (img) => {
    const currentIndex = images.findIndex(i => i.id === img.id);
    if (currentIndex > 0) {
      const newOrder = img.order - 1;
      try {
        await carouselAPI.update(img.id, { order: newOrder });
        fetchImages();
      } catch (error) {
        console.error('Error moving image:', error);
      }
    }
  };

  const handleMoveDown = async (img) => {
    const currentIndex = images.findIndex(i => i.id === img.id);
    if (currentIndex < images.length - 1) {
      const newOrder = img.order + 1;
      try {
        await carouselAPI.update(img.id, { order: newOrder });
        fetchImages();
      } catch (error) {
        console.error('Error moving image:', error);
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading carousel images...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Add Button */}
      {!isAdding && (
        <div>
          <Button
            onClick={() => setIsAdding(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Carousel Image
          </Button>
        </div>
      )}

      {/* Add Form */}
      {isAdding && (
        <Card className="border-2 border-emerald-300">
          <CardHeader className="bg-emerald-50">
            <CardTitle>Add New Carousel Image</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <ImageUploader
                value={newImage.url}
                onChange={(url) => setNewImage({ ...newImage, url })}
                label="Image *"
                placeholder="https://images.unsplash.com/..."
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newImage.title}
                  onChange={(e) => setNewImage({ ...newImage, title: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="e.g., St. Lawrence River"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Order
                </label>
                <input
                  type="number"
                  value={newImage.order}
                  onChange={(e) => setNewImage({ ...newImage, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleAdd}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Image
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewImage({ url: '', title: '', order: 0, active: true });
                  }}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Images Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((img, index) => (
          <Card key={img.id} className="border-2">
            <CardContent className="pt-6">
              {editingId === img.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <ImageUploader
                    value={editForm.url}
                    onChange={(url) => setEditForm({ ...editForm, url })}
                    label="Image"
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveEdit}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingId(null);
                        setEditForm({});
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <img src={img.url} alt={img.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                  <h3 className="font-bold text-lg mb-2">{img.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">Order: {img.order}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(img)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveUp(img)}
                      disabled={index === 0}
                    >
                      <MoveUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveDown(img)}
                      disabled={index === images.length - 1}
                    >
                      <MoveDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600"
                      onClick={() => handleDelete(img.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
